import { useNavigate } from 'react-router-dom';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  useTheme,
  Grid,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { pricingPlans } from '../../core';

type SubscribeModalProps = {
  closeModal: () => void;
  handleClose: () => void;
  message: string;
  title: string;
};

const PREMIUM_FEATURES = pricingPlans.find(
  (plan) => plan.id === 'premium',
)?.description;

export default function SubscribeModal({
  closeModal,
  handleClose,
  message,
  title,
}: SubscribeModalProps) {
  const navigate = useNavigate();
  const theme = useTheme();

  const handleRedirect = () => {
    handleClose();
    navigate('/pricing');
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <IconButton
        onClick={closeModal}
        sx={{
          position: 'absolute',
          right: 12,
          top: 12,
          color: 'primary.contrastText',
          zIndex: 10,
          bgcolor: 'rgba(0,0,0,0.1)',
          '&:hover': { bgcolor: 'rgba(0,0,0,0.2)' },
        }}
      >
        <ClearIcon fontSize="small" />
      </IconButton>
      <Box
        sx={{
          width: '100%',
          py: 6,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            width: '140%',
            height: '140%',
            background:
              'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
            top: '-20%',
            left: '-20%',
          }}
        />
        <AutoAwesomeIcon
          sx={{ fontSize: 44, mb: 1, zIndex: 1, opacity: 0.9 }}
        />
        <Typography
          variant="overline"
          sx={{
            fontWeight: 700,
            letterSpacing: 3,
            zIndex: 1,
            opacity: 0.9,
            textTransform: 'uppercase',
          }}
        >
          Premium Access
        </Typography>
      </Box>
      <Stack spacing={4} sx={{ p: { xs: 4, md: 6 }, textAlign: 'center' }}>
        {message && (
          <Box
            sx={{
              bgcolor: 'primary.50',
              color: 'primary.dark',
              py: 1,
              px: 2,
              borderRadius: '50px',
              display: 'inline-flex',
              alignItems: 'center',
              alignSelf: 'center',
              gap: 1,
              border: '1px solid',
              borderColor: 'primary.100',
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {message}
            </Typography>
          </Box>
        )}
        <Box>
          <Typography
            variant="h4"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontWeight: 800,
              color: 'text.primary',
              mb: 2,
              lineHeight: 1.1,
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              maxWidth: '340px',
              mx: 'auto',
              mb: 3,
            }}
          >
            Unlock the full potential of your heritage collection with our
            professional preservation suite.
          </Typography>
          <Box
            sx={{
              bgcolor: 'action.hover',
              borderRadius: 4,
              p: 3,
              textAlign: 'left',
              border: '1px border',
              borderColor: 'divider',
            }}
          >
            <Grid container spacing={2}>
              {PREMIUM_FEATURES?.map((feature) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  key={feature}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
                >
                  <CheckCircleIcon
                    sx={{ color: 'primary.main', fontSize: 20 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 500, color: 'text.primary' }}
                  >
                    {feature}
                  </Typography>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
        <Box sx={{ width: '100%' }}>
          <Button
            variant="contained"
            size="large"
            fullWidth
            onClick={handleRedirect}
            sx={{
              py: 2,
              borderRadius: 2,
              fontSize: '1rem',
              fontWeight: 700,
              textTransform: 'none',
              boxShadow: theme.shadows[4],
              mb: 1,
            }}
          >
            Upgrade Now
          </Button>
          <Button
            variant="text"
            fullWidth
            onClick={closeModal}
            sx={{
              color: 'text.disabled',
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { color: 'text.primary', bgcolor: 'transparent' },
            }}
          >
            Not now, continue with limited access
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}
